"use client";

import { useEffect, useRef, useState } from "react";
import {
  iGenericAccessDetail,
  iIdentifierFieldValidation,
  iProProject,
  iProProjectMain,
  ValidationOption,
  ValidationType 
} from "@/app/models/interfaceProject";
import { useUser } from "@/app/context/UserContext";
import { ChevronDown, Eye, FileUp, Pencil, ReceiptRussianRuble, Trash2, X } from "lucide-react";

import { AlertType } from "@/app/constants/enums";
import Modal from "../../Elements/Modal";
import PermissionsList from "./PermissionsList";
import { getValidationOptions } from "@/app/constants/defaultValue";
import ToggleSwitch from "../../Elements/Core/ToggleSwitch";



const VALIDATION_OPTIONS  = getValidationOptions();

export default function IdentifierFieldConfigForm() {
    const { userProject, setUserProject, addAlert} = useUser();
    const detail = userProject?.project?.generic_access_detail;

    const [label, setLabel] = useState(detail?.label ?? "");
    const [placeholder, setPlaceholder] = useState(detail?.placeholder ?? "");
    const [validations, setValidations] = useState<iIdentifierFieldValidation[]>( detail?.validation ?? [] );

    const [selectedValidation, setSelectedValidation] = useState<ValidationOption | null>(null);
    const [tempValue, setTempValue] = useState("");    

    const [permissionList, setPermissionList] = useState<string[]>([]); 
    const [viewPermissionModal, setViewPermissionModal] = useState(false);
    const [typePermissionModal, setTypePermissionModal] = useState<string>(""); 
    useEffect(() => {
        setUserProject((prev: iProProjectMain) => ({
            ...prev,
            project: {
                ...prev.project,
                generic_access: true,
                generic_access_detail: {
                    label,
                    placeholder,
                    validation: validations,
                },
            },
        }));
    }, [label, placeholder, validations]);
 
    const addValidation = () => {
        if (!selectedValidation) return;        
        if ( ["minLength", "maxLength", "pattern", "min", "max"].includes( selectedValidation.tipo ) && !tempValue.trim() ) {
            addAlert("Ingrese el valor de la validación", AlertType.Warning);
            return;
        } 
        const isNumeric = (value: string) => {
            if (!value) return false;
            return !isNaN(Number(value));
        };
        if ( ["minLength", "maxLength", "min", "max"].includes( selectedValidation.tipo ) && !isNumeric(tempValue.trim()) ) {
            addAlert("Ingrese un valor numerico", AlertType.Warning);
            return;
        } 

        setValidations((prev) => [
            ...prev,
            {
                tipo: selectedValidation.tipo,
                caracteristica: selectedValidation.requiresValue ? tempValue : undefined,
                permissions_list:[]
            },
        ]);  
        setPermissionList([]);
        setViewPermissionModal(false);
        setSelectedValidation(null);
        setTempValue("");

    };

    const removeValidation = (tipo: ValidationType) => {
        setValidations((prev) => prev.filter((v) => v.tipo !== tipo));
    };  

    const handleSavePermissionList = (list: string[]) => { 
        if (list.length === 0) {
            addAlert("Ingrese valores en el listado de permisos", AlertType.Warning);
            return;
        }
        if(typePermissionModal==="edit"){
            setValidations((prev) =>
                    prev.map((v) =>
                        v.tipo === "list"
                            ? {
                                ...v,
                                permissions_list: list,
                            }
                            : v
                    )
                );

        }else{
            setValidations((prev) => [
                ...prev,
                {
                    tipo: "list",
                    caracteristica: "",
                    permissions_list: list,
                },
            ]);

        }
        setPermissionList(list);
        setViewPermissionModal(false);
        setSelectedValidation(null);
        setTempValue("");
    };
    
    const handleGenericAccess = (checked: boolean) => {
        setUserProject((prev: any) => ({
        ...prev,
        project: {
            ...prev.project,
            generic_access: checked,
            
        },
        }));
    };
    

    const [genericAccess, setGenericAccess] = useState<boolean>(false);
    useEffect(() => {
        setGenericAccess(!!userProject?.project?.generic_access);
    }, [userProject?.project?.generic_access]);
    
  return (
    <div className=" space-y-2">
   
        <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold">{(userProject.project.generic_access ?? false)&&("Configuración del identificador")}</h2>

            <div className="flex items-center gap-3 ">
                <span>Activar identificador</span>
                <ToggleSwitch
                    checked={userProject.project.generic_access ?? false}
                    onChange={handleGenericAccess}
                />
            </div>
        </div>
        {(userProject.project.generic_access ?? false)&&(
            <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 px-1 py-1">
                    <div> 
                    <label className="block text-gray-700">Etiqueta *</label>
                    <input
                        type="text" 
                        value={label} 
                        onChange={(e) => setLabel(e.target.value)} 
                        required
                        className={`w-full p-2 border n-fmr-input  border-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-700`}
                        disabled={!genericAccess}
                    />
                    </div> 
                    <div> 
                        <label className="block text-gray-700">Placeholder</label>
                        <input
                            type="text" 
                            value={placeholder} 
                            onChange={(e) => setPlaceholder(e.target.value)}  
                            className={`w-full p-2 border n-fmr-input  border-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-700`}
                            disabled={!genericAccess}
                        />        
                    </div>
                </div>            
                <div className={`grid grid-cols-${(selectedValidation?.tipo=='list' || !selectedValidation?.requiresValue )?'2':'3'}  px-1 py-1`} >
                    <div className="mr-2">
                        <label className="block text-gray-700">Agregar validación</label>                    
                        <div className="relative"> 
                            <select 
                                className= {`appearance-none w-full border n-fmr-input px-4 pr-4 py-2 border-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-700 
                                    disabled:bg-gray-100 disabled:text-gray-400 disabled:border-gray-200 `}
                                value={selectedValidation?.tipo ?? ""}
                                onChange={(e) =>{
                                        setSelectedValidation(
                                            VALIDATION_OPTIONS.find((v) => v.tipo === e.target.value) ?? null
                                        )
                                        setPermissionList([]);
                                        setViewPermissionModal(false);
                                        setTempValue("");
                                    }
                                }
                                disabled={!genericAccess}
                            >
                                <option value="">Seleccionar</option>
                                {VALIDATION_OPTIONS.filter(
                                (opt) => !validations.some((v) => v.tipo === opt.tipo)
                                ).map((opt) => (
                                <option key={opt.tipo} value={opt.tipo}>
                                    {opt.label}
                                </option>
                                ))}
                            </select>
                            <ChevronDown className="w-5 h-5 absolute inset-y-0 top-3 right-3 pointer-events-none text-gray-400" /> 
                        </div>
                    </div>
                    {selectedValidation?.requiresValue && (
                        selectedValidation.tipo=='list'?
                        <div>
                        
                            <button
                                type="button"
                                onClick={() => {setTypePermissionModal("add");setViewPermissionModal(true);}}
                                className={`mt-6 flex items-center btn-inca-secondary justify-center px-2 py-2 h-10 rounded-full bg-gray-100 transition-colors border shadow-md  hover:bg-gray-200 disabled:bg-gray-100`}
                                disabled={!genericAccess}
                            >
                                <FileUp className="w-6 h-6 mr-2" strokeWidth={1.5} /> 
                                Agregar Permisos
                            </button>
                        </div>
                        :
                        <div>
                            <label className="block text-gray-700">Característica de validación</label>                    
                            <input
                            className={`w-full p-2 border n-fmr-input  border-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-700`}
                            placeholder={selectedValidation.placeholder}
                            value={tempValue}
                            onChange={(e) => setTempValue(e.target.value)}
                            disabled={!genericAccess}
                            />
                            
                        </div>

                    
                    )}


                    { selectedValidation&&selectedValidation?.tipo!='list'&&(
                        <div>
                            <button 
                                className={`mt-6 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 n-fmr-input focus:outline-none focus:ring-1 focus:ring-blue-700 ml-2`}
                                type="button" 
                                onClick={addValidation}
                                disabled={!genericAccess}
                            >
                                Agregar validación
                            </button> 
                        </div>
                    )}
                    

                </div>    
                <div className="space-y-3"> 
                    {validations.map((v, index) => (            
                        <div key={index} className="flex justify-between items-center w-full text-sm border rounded-2xl p-1 pl-4 pr-4  bg-white">
                            
                            <span className="flex-1"><span className="font-semibold"> {v.tipo}</span> {v.tipo=='list'? ` (${(v.permissions_list??[]).length} filas)`:(v.caracteristica && `(${v.caracteristica})`) }</span>
                            <div className="flex space-x-2"> 
                                {v.tipo=='list'&&(
                                    <button type="button" onClick={() => {
                                        setTypePermissionModal("edit");
                                        setViewPermissionModal(true);
                                        setPermissionList(v.permissions_list??[]);
                                    }} 
                                        className="btn-inca-secondary shadow-md rounded-full w-9 h-9 border border-gray-300" 
                                        disabled={!genericAccess}
                                    >
                                        <Eye  className="w-4 h-4 m-2" strokeWidth={1.5} />
                                    </button>
                                )}
                                <button type="button" onClick={() => removeValidation(v.tipo)} 
                                    className="btn-inca-secondary shadow-md rounded-full w-9 h-9 border border-gray-300"
                                    disabled={!genericAccess}
                                >
                                    <X  className="w-4 h-4 m-2" strokeWidth={1.5} />
                                </button>
                            </div>
                    </div>
                    ))} 
                </div>
                <Modal
                    isOpen={viewPermissionModal}
                    onClose={() => setViewPermissionModal(false)}
                    btnClose={true}
                    >
                        <PermissionsList
                            value={permissionList} 
                            onChange={setPermissionList}
                            onSave={(list)=>handleSavePermissionList(list)}
                            onClose={() => setViewPermissionModal(false)}
                        /> 
                </Modal>
            </>
        )}
        

    </div>
  );
}
